import { useMemo } from "react";
import { CalendarDays, Copy, MapPin } from "lucide-react";
import { Loader } from "components/core/Common/Loader";
import SearchBarRed from "components/shared/SearchBar/SearchBarRed";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import { DesktopPageTabsBar } from "components/shared/PageTabs/PageTabs";
import TableWithFilters from "components/shared/TableWithFilters/TableWithFilters";
import FiltersPanel from "components/shared/TableWithFilters/FiltersPanel";
import FilterSelect from "components/shared/TableWithFilters/FilterSelect";
import { FilterLabel } from "components/shared/TableWithFilters/styles.tableWithFilters";
import {
  StateContainer,
  StateImage,
  StateMessage,
  StateTitle,
  StateButton,
} from "components/core/PO/PurchaseOrderList/styles";
import { DesktopPage, Title, TitleRow } from "components/core/Common/DesktopHelpers/DesktopHelpers";
import {
  PoIdLink,
  CellText,
} from "components/core/PO/PurchaseOrderList/styles.purchaseOrderListDesktop";
import usePlanShipmentListLogic, {
  PO_STATUS_OPTIONS,
  PRIMARY_TAB_CONFIG,
  SHIPMENT_TYPE_OPTIONS,
  SHIPPING_NOTE_STATUS_OPTIONS,
} from "./usePlanShipmentListLogic";
import {
  CellHint,
  CellStack,
  CopyIconButton,
  DisabledPoId,
  FilterFieldFixed,
  IconCell,
  ReferenceCell,
  StateBox,
  StatusTag,
  CellMuted,
} from "./styles.planShipmentDesktop";

const getStatusTone = (status = "") => {
  const normalized = status.toLowerCase();
  if (normalized.includes("fulfilled") || normalized === "arrived") return "green";
  if (normalized.includes("cancel") || normalized.includes("no show")) return "grey";
  if (normalized.includes("unscheduled") || normalized.includes("partially scheduled")) return "orange";
  return "blue";
};

const StatusCell = ({ status }) => <StatusTag $tone={getStatusTone(status)}>{status}</StatusTag>;

const DeliverByCell = ({ value }) => (
  <IconCell>
    <CalendarDays size={16} />
    <CellText>{value}</CellText>
  </IconCell>
);

const DeliveryAtCell = ({ value }) => (
  <IconCell>
    <MapPin size={16} />
    <CellText>{value}</CellText>
  </IconCell>
);

const textColumn = (id, header, width, accessor) => ({
  id,
  header,
  meta: { width },
  cell: (info) => <CellText>{accessor(info.row.original)}</CellText>,
});

const useColumns = ({ isPoTab, isScheduledView, openPurchaseOrder, openScheduleGroup, openShippingNote }) =>
  useMemo(() => {
    if (!isPoTab) {
      return [
        {
          id: "note_id",
          header: "Shipping note ID",
          meta: { width: 200 },
          cell: (info) => (
            <PoIdLink>{info.row.original.noteNumber}</PoIdLink>
          ),
        },
        {
          id: "delivery_at",
          header: "Delivery at",
          meta: { width: 210 },
          cell: (info) => <DeliveryAtCell value={info.row.original.deliveryAt} />,
        },
        {
          id: "deliver_by",
          header: "Deliver by",
          meta: { width: 160 },
          cell: (info) => <DeliverByCell value={info.row.original.deliverBy} />,
        },
        textColumn("slot", "Delivery slot", 150, (row) => row.slot),
        textColumn("invoices", "Invoices", 110, (row) => row.invoices),
        textColumn("net_weight", "Net weight", 120, (row) => row.netWeight),
        textColumn("shipment_type", "Shipment type", 170, (row) => row.shipmentType),
        {
          id: "reference",
          header: "Reference or Vehicle#",
          meta: { width: 220 },
          cell: (info) => {
            const { reference } = info.row.original;
            return (
              <ReferenceCell>
                <CellStack>
                  <CellText>{reference.primary}</CellText>
                  <CellMuted>{reference.secondary}</CellMuted>
                </CellStack>
                <CopyIconButton
                  type="button"
                  aria-label="Copy reference"
                  onClick={() => navigator.clipboard?.writeText(reference.primary)}
                >
                  <Copy size={16} />
                </CopyIconButton>
              </ReferenceCell>
            );
          },
        },
      ];
    }

    if (isScheduledView) {
      return [
        {
          id: "group_id",
          header: "Schedule group ID",
          meta: { width: 200 },
          cell: (info) => (
            <PoIdLink>{info.row.original.groupNumber}</PoIdLink>
          ),
        },
        textColumn("po_count", "POs", 100, (row) => row.poCount),
        textColumn("net_weight", "Net weight", 120, (row) => row.netWeight),
        {
          id: "deliver_by",
          header: "Deliver by",
          meta: { width: 160 },
          cell: (info) => <DeliverByCell value={info.row.original.deliverBy} />,
        },
        textColumn("slot", "Delivery slot", 150, (row) => row.slot),
        {
          id: "delivery_at",
          header: "Delivery at",
          meta: { width: 210 },
          cell: (info) => <DeliveryAtCell value={info.row.original.deliveryAt} />,
        },
        {
          id: "status",
          header: "Status",
          meta: { width: 200 },
          cell: (info) => <StatusCell status={info.row.original.status} />,
        },
      ];
    }

    return [
      {
        id: "po_id",
        header: "PO ID",
        meta: { width: 220 },
        cell: (info) => {
          const row = info.row.original;
          return (
            <CellStack>
              {row.isSchedulable ? (
                <PoIdLink>{row.poNumber}</PoIdLink>
              ) : (
                <>
                  <DisabledPoId>{row.poNumber}</DisabledPoId>
                  <CellHint>Scheduling enabled after {row.schedulingEnabledDate}</CellHint>
                </>
              )}
            </CellStack>
          );
        },
      },
      textColumn("skus", "SKUs", 120, (row) => row.skus),
      textColumn("net_weight", "Net weight", 120, (row) => row.netWeight),
      {
        id: "deliver_by",
        header: "Deliver by",
        meta: { width: 160 },
        cell: (info) => <DeliverByCell value={info.row.original.deliverBy} />,
      },
      textColumn("po_amount", "PO amount", 140, (row) => row.amount),
      {
        id: "delivery_at",
        header: "Delivery at",
        meta: { width: 210 },
        cell: (info) => <DeliveryAtCell value={info.row.original.deliveryAt} />,
      },
      {
        id: "status",
        header: "Status",
        meta: { width: 200 },
        cell: (info) => <StatusCell status={info.row.original.status} />,
      },
    ];
  }, [isPoTab, isScheduledView, openPurchaseOrder, openScheduleGroup, openShippingNote]);

const getSearchPlaceholder = (isPoTab, isScheduledView) => {
  if (!isPoTab) return "Search by ASN ID";
  return isScheduledView ? "Search by schedule group ID" : "Search by PO ID";
};

const getEmptyCopy = (isPoTab, isScheduledView, isSearching) => {
  if (isSearching) {
    return { title: "No matching results", message: "Try a different ID." };
  }
  if (!isPoTab) return { title: "All caught up!", message: "No shipping notes are available." };
  return isScheduledView
    ? { title: "All caught up!", message: "There are no schedule groups." }
    : { title: "All caught up!", message: "There are no purchase orders to schedule." };
};

export default function PlanShipmentDesktop() {
  const vm = usePlanShipmentListLogic();
  const {
    activeTab,
    isPoTab,
    isScheduledView,
    page,
    searchQuery,
    isSearching,
    hasValidVendorId,
    isLoading,
    hasError,
    errorMessage,
    hasNextPage,
    rows,
  } = vm;

  const columns = useColumns(vm);

  const filters = (
    <FiltersPanel
      onApply={vm.applyFilters}
      onClear={vm.clearFilters}
      applyDisabled={!vm.hasPendingFilterChanges}
    >
      {isPoTab ? (
        <FilterFieldFixed>
          <FilterLabel>Status</FilterLabel>
          <FilterSelect
            value={vm.draftPoStatus}
            options={PO_STATUS_OPTIONS}
            onChange={vm.setDraftPoStatus}
            placeholder="Select status"
          />
        </FilterFieldFixed>
      ) : (
        <>
          <FilterFieldFixed>
            <FilterLabel>Status</FilterLabel>
            <FilterSelect
              value={vm.draftNoteStatus}
              options={SHIPPING_NOTE_STATUS_OPTIONS}
              onChange={vm.setDraftNoteStatus}
              placeholder="Select status"
            />
          </FilterFieldFixed>
          <FilterFieldFixed>
            <FilterLabel>Shipment type</FilterLabel>
            <FilterSelect
              value={vm.draftShipmentType}
              options={SHIPMENT_TYPE_OPTIONS}
              onChange={vm.setDraftShipmentType}
              placeholder="Select type"
            />
          </FilterFieldFixed>
        </>
      )}
    </FiltersPanel>
  );

  const renderState = () => {
    if (!hasValidVendorId) {
      return (
        <>
          <StateTitle>Unable to load data</StateTitle>
          <StateMessage>Please select a valid outlet and try again.</StateMessage>
        </>
      );
    }
    if (isLoading) return <Loader />;
    if (hasError) {
      return (
        <>
          <StateTitle>Unable to load data</StateTitle>
          <StateMessage>{errorMessage || "Please check your connection and try again."}</StateMessage>
          <StateButton type="button" onClick={vm.refresh}>
            Try again
          </StateButton>
        </>
      );
    }
    const { title, message } = getEmptyCopy(isPoTab, isScheduledView, isSearching);
    return (
      <>
        <StateImage src="/logos/po_empty.svg" alt="" />
        <StateTitle>{title}</StateTitle>
        <StateMessage>{message}</StateMessage>
      </>
    );
  };

  // Filters stay visible in every state so the user can always switch status back.
  const renderContent = () => {
    if (!hasValidVendorId || hasError || rows.length === 0) {
      return (
        <>
          {filters}
          <StateBox>
            <StateContainer>{renderState()}</StateContainer>
          </StateBox>
        </>
      );
    }
    const handleRowClick = (row) => {
      if (!isPoTab) return vm.openShippingNote(row._raw);
      if (isScheduledView) return vm.openScheduleGroup(row._raw);
      if (row.isSchedulable) vm.openPurchaseOrder(row._raw);
    };

    return (
      <TableWithFilters
        filters={filters}
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        emptyMessage="No results found"
        page={page}
        hasNextPage={hasNextPage}
        onPageChange={vm.goToPage}
        onRowClick={handleRowClick}
      />
    );
  };

  return (
    <DesktopPage>
      <Breadcrumb items={[{ label: "Home" }, { label: "Plan Shipment" }]} />
      <TitleRow>
        <Title>Plan shipment</Title>
        <SearchBarRed
          placeholder={getSearchPlaceholder(isPoTab, isScheduledView)}
          value={searchQuery}
          inputMode="numeric"
          onChange={(event) => vm.setSearchQuery(event.target.value)}
        />
      </TitleRow>
      <DesktopPageTabsBar
        tabs={PRIMARY_TAB_CONFIG}
        activeKey={activeTab}
        onChange={vm.handleTabChange}
        ariaLabel="Plan shipment pages"
      />
      {renderContent()}
    </DesktopPage>
  );
}
